import { useState } from 'react';

export default function ManagedITPage() {
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showCustomerToolsDropdown, setShowCustomerToolsDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const form = e.currentTarget;
      const formDataObj = new FormData(form);

      const message = formDataObj.get('message') as string;
      if (message && message.length > 500) {
        setSubmitStatus({
          type: 'error',
          message: 'Mesaj 500 karakterden uzun olamaz.',
        });
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(
        'https://lbvcnepwlojmojlxmhau.supabase.co/functions/v1/send-contact-email',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fullName: formDataObj.get('fullName'),
            phone: formDataObj.get('phone'),
            email: formDataObj.get('email'),
            company: formDataObj.get('company'),
            message: formDataObj.get('message'),
          }),
        }
      );

      let result: any;
      try {
        result = await response.json();
      } catch (jsonError) {
        throw new Error('Sunucudan geçerli bir yanıt alınamadı.');
      }

      if (result?.success) {
        setSubmitStatus({
          type: 'success',
          message:
            'Talebiniz başarıyla gönderildi. En kısa sürede sizinle iletişime geçeceğiz.',
        });
        form.reset();
        setTimeout(() => {
          setShowCallbackModal(false);
          setSubmitStatus(null);
        }, 2000);
      } else {
        throw new Error(result?.error || 'Form gönderimi başarısız');
      }
    } catch (error) {
      console.error('Form submit error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Form gönderilirken bir hata oluştu. Lütfen tekrar deneyin.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setShowServicesDropdown(false);
    setShowCustomerToolsDropdown(false);
  };

  const services = [
    {
      icon: 'ri-shield-check-line',
      title: 'Proaktif IT İzleme',
      description: 'Sistemlerinizi 7/24 izleyerek sorunları oluşmadan önce tespit ediyor ve çözüyoruz.',
      features: ['Gerçek zamanlı sistem izleme', 'Otomatik uyarı sistemleri', 'Performans raporlaması', 'Kapasite planlama']
    },
    {
      icon: 'ri-tools-line',
      title: 'IT Bakım ve Destek',
      description: 'Tüm IT altyapınızın kesintisiz çalışması için kapsamlı bakım ve destek hizmeti.',
      features: ['Düzenli sistem güncellemeleri', 'Güvenlik yamaları', 'Donanım bakımı', 'Yazılım optimizasyonu']
    },
    {
      icon: 'ri-cloud-line',
      title: 'Bulut Yönetimi',
      description: 'Hibrit ve tam bulut altyapılarınızı profesyonel olarak yönetiyoruz.',
      features: ['Multi-cloud yönetimi', 'Maliyet optimizasyonu', 'Güvenlik yönetimi', 'Yedekleme stratejileri']
    },
    {
      icon: 'ri-user-settings-line',
      title: 'Kullanıcı Desteği',
      description: 'Çalışanlarınıza teknik konularda hızlı ve etkili destek sağlıyoruz.',
      features: ['Help desk hizmeti', 'Uzaktan destek', 'Eğitim ve rehberlik', 'Problem çözme']
    },
    {
      icon: 'ri-database-2-line',
      title: 'Veri Yönetimi',
      description: 'Verilerinizin güvenliği, yedeklenmesi ve kurtarılması için kapsamlı çözümler.',
      features: ['Otomatik yedekleme', 'Felaket kurtarma', 'Veri güvenliği', 'Arşivleme çözümleri']
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Raporlama ve Analiz',
      description: 'IT performansınızı detaylı raporlarla takip edin ve stratejik kararlar alın.',
      features: ['Aylık performans raporları', 'Maliyet analizleri', 'Sistem metrikleri', 'İyileştirme önerileri']
    }
  ];

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Maliyet Tasarrufu',
      description: 'Kendi IT ekibiniz kurmaktan %60\'a kadar daha düşük maliyetle profesyonel hizmet alın.'
    },
    {
      icon: 'ri-focus-3-line',
      title: 'Ana İşinize Odaklanın',
      description: 'IT yönetimini bize bırakarak ana işinize ve büyüme stratejilerinize odaklanın.'
    },
    {
      icon: 'ri-user-star-line',
      title: 'Uzman Ekip',
      description: 'Sertifikalı uzmanlarımızla en güncel teknolojileri ve en iyi uygulamaları kullanın.'
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Güvenlik ve Uyumluluk',
      description: 'KVKK, ISO 27001 ve sektörel düzenlemelere uygun güvenli IT altyapısı.'
    }
  ];

  const plans = [
    {
      name: 'Temel Paket',
      price: '₺2.500',
      period: '/ay',
      description: 'Küçük işletmeler için temel IT yönetimi',
      features: [
        '10 kullanıcıya kadar destek',
        'Temel sistem izleme',
        'E-posta desteği',
        'Aylık sistem raporu',
        'Temel güvenlik yönetimi',
        'Çalışma saatleri desteği'
      ],
      highlighted: false
    },
    {
      name: 'Profesyonel Paket',
      price: '₺4.500',
      period: '/ay',
      description: 'Orta ölçekli şirketler için kapsamlı çözüm',
      features: [
        '25 kullanıcıya kadar destek',
        'Gelişmiş sistem izleme',
        'Telefon ve e-posta desteği',
        'Haftalık sistem raporu',
        'Kapsamlı güvenlik yönetimi',
        'Öncelikli destek',
        'Uzaktan erişim yönetimi',
        'Veri yedekleme servisi'
      ],
      highlighted: true
    },
    {
      name: 'Kurumsal Paket',
      price: 'Özel Fiyat',
      period: '',
      description: 'Büyük kurumlar için tam kapsamlı hizmet',
      features: [
        'Sınırsız kullanıcı desteği',
        '7/24 proaktif izleme',
        'Özel hesap yöneticisi',
        'Günlük detay raporları',
        'Kurumsal güvenlik çözümleri',
        '7/24 acil destek',
        'Özel SLA anlaşması',
        'Yerinde teknik destek',
        'Felaket kurtarma planı'
      ],
      highlighted: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-gray-900 sticky top-0 z-50 shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-8">
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="flex items-center space-x-3 cursor-pointer"
              >
                <img
                  src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7b7be6e694aa19d5c5f7abd74de9f9de.png"
                  alt="Nimbus Bilişim Logo"
                  className="h-24 w-auto"
                />
                <div className="text-lg text-gray-900">
                  Nimbus Bulut Bilişim Teknoloji ve Mühendislik
                </div>
              </button>

              <nav className="hidden lg:flex space-x-8">
                <div className="relative group">
                  <button
                    onClick={() => {
                      setShowServicesDropdown(!showServicesDropdown);
                      setShowCustomerToolsDropdown(false);
                    }}
                    className="flex items-center space-x-1 hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                  >
                    <span>IT Hizmetleri</span>
                    <i
                      className={`ri-arrow-${showServicesDropdown ? 'up' : 'down'}-s-line transition-transform`}
                    ></i>
                  </button>
                  {showServicesDropdown && (
                    <div className="absolute top-full left-0 mt-2 w-[1000px] bg-white rounded-lg shadow-lg border border-gray-200 py-6 z-50">
                      <div className="grid grid-cols-3 gap-8 px-8">
                        {/* IT Destek Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            IT Destek Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/it-support');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">IT Destek & Bakım Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">7/24 IT destek ve bakım hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/it-consulting');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">IT Danışmanlık Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Profesyonel danışmanlık</div>
                            </button>
                          </div>
                        </div>

                        {/* Bulut Bilişim Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            Bulut Bilişim Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/virtual-server');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Bulut Bilişim Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Kurumsal bulut sunucu hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/managed-it');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-blue-600 font-semibold cursor-pointer"
                            >
                              <div className="font-medium">Yönetilen IT Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Tüm yönetim hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/migration');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Sistem Taşıma ve Geçiş Hizmetleri (Migration)</div>
                              <div className="text-sm text-gray-500 mt-1">Sunucu taşıma hizmetleri</div>
                            </button>
                          </div>
                        </div>

                        {/* Microsoft 365 Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            Microsoft 365 Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/microsoft-365');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Microsoft 365 Geçiş ve Yönetimi</div>
                              <div className="text-sm text-gray-500 mt-1">Azure Expert hizmetleri</div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                >
                  Başarı Hikayeleri
                </button>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/about')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                >
                  Hakkımızda
                </button>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                >
                  SSS
                </button>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowCallbackModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                İletişim
              </button>
              <a
                href="https://www.linkedin.com/company/nimbus-bulut-bili%C5%9Fim-teknoloji-yaz%C4%B1l%C4%B1m-ve-m%C3%BChendislik-a-%C5%9F/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer"
              >
                <i className="ri-linkedin-fill text-2xl"></i>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Click outside to close dropdowns */}
      {(showServicesDropdown || showCustomerToolsDropdown) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowServicesDropdown(false);
            setShowCustomerToolsDropdown(false);
          }}
        ></div>
      )}

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowMobileMenu(false)}
          ></div>
          <div className="absolute right-0 top-0 h-full w-80 bg-white shadow-xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-bold text-gray-900">Menü</h3>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <nav className="space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    IT Hizmetleri
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/it-support');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">
                        IT Destek & Bakım Hizmetleri
                      </div>
                      <div className="text-sm text-gray-500">
                        7/24 IT destek ve bakım hizmetleri (üzatkan & yerinde)
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">IT Danışmanlık Hizmetleri</div>
                      <div className="text-sm text-gray-500">
                        Projeleriniz için profesyonel danışmanlık
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/virtual-server');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Bulut Bilişim Hizmetleri</div>
                      <div className="text-sm text-gray-500">
                        İşletmeniz için tam kapsamlı kurumsal bulut sunucu hizmetleri
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/managed-it');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-blue-600 font-semibold cursor-pointer"
                    >
                      <div className="font-medium">Yönetilen IT Hizmetleri</div>
                      <div className="text-sm text-gray-500">
                        Tüm yönetim hizmetleri
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/microsoft-365');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Microsoft 365 Geçiş ve Yönetimi</div>
                      <div className="text-sm text-gray-500">
                        Azure projeleriniz için Azure Expert hizmetleri
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Sistem Taşıma ve Geçiş</div>
                      <div className="text-sm text-gray-500">
                        Sunucu taşıma sürecinizde tam kontrol
                      </div>
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    Şirket
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      Başarı Hikayeleri
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/about');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      Hakkımızda
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      SSS
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    Müşteri Araçları
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Müşteri Portalı</div>
                      <div className="text-sm text-gray-500">
                        Fatura ve destek yönetimi
                      </div>
                    </button>
                    <a
                      href="mailto:destek@nimbusbilisim.com"
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer block"
                    >
                      <div className="font-medium">7/24 Destek Hattı</div>
                      <div className="text-sm text-gray-500">
                        destek@nimbusbilisim.com
                      </div>
                    </a>
                    <a
                      href="https://rustdesk.com/download"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer block"
                    >
                      <div className="font-medium">RustDesk İndir</div>
                      <div className="text-sm text-gray-500">
                        Uzaktan destek programı
                      </div>
                    </a>
                    <button
                      onClick={() => {
                        setShowCallbackModal(true);
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Geri Arama Talebi</div>
                      <div className="text-sm text-gray-500">
                        Uzmanlarımızla görüşün
                      </div>
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <button
                    onClick={() => {
                      setShowCallbackModal(true);
                      setShowMobileMenu(false);
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    İletişim
                  </button>
                </div>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-20 overflow-hidden">
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/372bd53cebb6c2aa98734bbfae00e731.jpg')`,
            animation: 'slowPan 5s ease-in-out infinite alternate'
          }}
        ></div>
        <style>{`
          @keyframes slowPan {
            0% {
              transform: scale(1) translate(0, 0);
            }
            100% {
              transform: scale(1.1) translate(-3%, 3%);
            }
          }
        `}</style>
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-4xl">
            <p className="text-blue-400 text-lg mb-4">
              IT Yönetimini Uzmanlarına Bırakın
            </p>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Yönetilen IT Hizmetleri
            </h1>
            <p className="text-xl mb-8 leading-relaxed">
              İşletmenizin teknoloji ihtiyaçlarını karşılamak için kapsamlı ve 
              profesyonel IT yönetim hizmetleri sunuyoruz.
            </p>
            <button
              onClick={() => setShowCallbackModal(true)}
              className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center space-x-2 whitespace-nowrap cursor-pointer"
            >
              <span>Ücretsiz Danışmanlık Alın</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Yönetilen IT Hizmetlerimiz
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              İşletmenizin teknoloji ihtiyaçlarını karşılamak için kapsamlı ve 
              profesyonel IT yönetim hizmetleri sunuyoruz.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className={`${service.icon} text-blue-600 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4 text-center">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-2 text-sm text-gray-600">
                      <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Neden Yönetilen IT Hizmetleri?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Kendi IT ekibiniz kurmak yerine uzman hizmet alarak hem tasarruf 
              edin hem de daha kaliteli hizmet alın.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className={`${benefit.icon} text-blue-600 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nasıl Çalışıyoruz?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Yönetilen IT hizmetlerimiz, işletmenizin ihtiyaçlarına özel olarak
              tasarlanmış bir süreçle sunulur
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Analiz ve Planlama
              </h3>
              <p className="text-gray-600">
                Mevcut IT altyapınızı analiz ediyor ve ihtiyaçlarınızı
                belirliyoruz
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-600">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Kurulum ve Entegrasyon
              </h3>
              <p className="text-gray-600">
                İzleme sistemlerini kurarak mevcut altyapınızla entegre ediyoruz
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Proaktif Yönetim
              </h3>
              <p className="text-gray-600">
                7/24 izleme ve proaktif müdahale ile sistemlerinizi koruyoruz
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-600">4</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Sürekli İyileştirme
              </h3>
              <p className="text-gray-600">
                Düzenli raporlama ve optimizasyon önerileriyle sürekli gelişim
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Yönetilen IT Hizmetleri Hakkında Daha Fazla Bilgi Alın
            </h3>
            <p className="text-gray-600 mb-8">
              Uzman ekibimiz, işletmenizin IT ihtiyaçlarını değerlendirmek ve size
              en uygun çözümü sunmak için hazır
            </p>
            <button
              onClick={() => setShowCallbackModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg transition-colors flex items-center space-x-2 mx-auto whitespace-nowrap cursor-pointer"
            >
              <span>Bizimle İletişime Geçin</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white text-gray-900 py-12 border-t border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img
                  src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7b7be6e694aa19d5c5f7abd74de9f9de.png"
                  alt="Nimbus Bilişim Logo"
                  className="h-24 w-auto"
                />
                <div className="text-lg">Nimbus Bulut Bilişim Teknoloji ve Mühendislik</div>
              </div>
              <p className="text-gray-600 mb-4">
                Türkiye'nin güvenilir IT çözüm ortağı. İşletmenizin teknoloji
                ihtiyaçlarında yanımızdayız.
              </p>
              <div className="flex space-x-4">
                <a
                  href="#"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a
                  href="#"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <i className="ri-twitter-fill text-xl"></i>
                </a>
                <a
                  href="#"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Hizmetler</h4>
              <ul className="space-y-2 text-gray-600">
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/services/virtual-server')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Sanal Sunucu
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/services/microsoft-365')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Microsoft 365
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/services/it-support')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    IT Destek
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/services/managed-it')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Yönetilen IT
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Şirket</h4>
              <ul className="space-y-2 text-gray-600">
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/about')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Hakkımızda
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Başarı Hikayeleri
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    SSS
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Müşteri Portalı
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">İletişim</h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center space-x-2">
                  <i className="ri-mail-line"></i>
                  <span>info@nimbusbilisim.com</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-map-pin-line"></i>
                  <span>İzmir, Türkiye</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-10 pt-10 text-center text-gray-600">
            <p>
              © 2025 Nimbus Bulut Bilişim Teknoloji ve Mühendislik. Tüm hakları saklıdır. |{' '}
              <a
                href="https://readdy.ai/?origin=logo"
                className="hover:text-blue-600 transition-colors"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Callback Modal */}
      {showCallbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  İletişim Formu
                </h3>
                <button
                  onClick={() => {
                    setShowCallbackModal(false);
                    setSubmitStatus(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {submitStatus && (
                <div
                  className={`mb-6 p-4 rounded-lg ${
                    submitStatus.type === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}

              <form
                onSubmit={handleFormSubmit}
                data-readdy-form
                id="managed-it-contact-form"
              >
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="fullName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Adınız ve soyadınız"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Telefon numaranız"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      E-posta *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="E-posta adresiniz"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Şirket
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      className="w-100 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Şirket adınız (opsiyonel)"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Mesaj
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-100 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                      placeholder="Mesajınızı buraya yazın (maksimum 500 karakter)"
                      onChange={handleInputChange}
                    ></textarea>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-100 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 rounded-lg transition-colors mt-6 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Gönderiliyor...' : 'Gönder'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
